import { apiSlice } from "./apiSlice";
import {
  LinkSandboxResourceBody,
  SandboxResourceRow,
  UpdateSandboxResourceBody,
} from "../../services/sandbox/sandbox.types";

/**
 * Admin-only endpoints for the resources published to the in-app testing
 * sandbox (SCRUM-649). The list is global: linking a resource exposes it to
 * every tester.
 */
export const sandboxResourcesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Resource types that can be published. Read from the backend rather than
     * hardcoded here so the picker stays correct when emmi-app gains a screen
     * for a type it cannot open today.
     */
    getSandboxSupportedTypes: builder.query<{ types: string[] }, void>({
      query: () => ({
        url: "/sandbox-resources/supported-types",
        method: "GET",
      }),
    }),

    getSandboxResources: builder.query<SandboxResourceRow[], void>({
      query: () => ({
        url: "/sandbox-resources/admin",
        method: "GET",
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "SandboxResource" as const,
                id,
              })),
              { type: "SandboxResource" as const, id: "LIST" },
            ]
          : [{ type: "SandboxResource" as const, id: "LIST" }],
    }),

    linkSandboxResource: builder.mutation<
      SandboxResourceRow,
      LinkSandboxResourceBody
    >({
      query: (body) => ({
        url: "/sandbox-resources",
        method: "POST",
        data: body,
      }),
      // Guarded so a failed mutation refetches nothing.
      invalidatesTags: (_result, error) =>
        error ? [] : [{ type: "SandboxResource" as const, id: "LIST" }],
    }),

    updateSandboxResource: builder.mutation<
      SandboxResourceRow,
      UpdateSandboxResourceBody
    >({
      query: ({ id, ...body }) => ({
        url: `/sandbox-resources/${id}`,
        method: "PATCH",
        data: body,
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : [{ type: "SandboxResource" as const, id: "LIST" }],
    }),

    unlinkSandboxResource: builder.mutation<{ success: boolean }, number>({
      query: (id) => ({
        url: `/sandbox-resources/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : [{ type: "SandboxResource" as const, id: "LIST" }],
    }),
  }),
});

export const {
  useGetSandboxSupportedTypesQuery,
  useGetSandboxResourcesQuery,
  useLinkSandboxResourceMutation,
  useUpdateSandboxResourceMutation,
  useUnlinkSandboxResourceMutation,
} = sandboxResourcesApi;
